import React, { useMemo } from "react";
import { Box, Typography, type TypographyProps } from "@mui/material";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import {
  htmlToPlainText,
  isPlainNoticeBody,
} from "../../utils/noticeBodyHtml";
import { sanitizeNoticeBodyHtml } from "../../utils/sanitizeNoticeBodyHtml";

type NoticeBodyHtmlProps = {
  body: string;
  /** When true, clamp height for list cards (CSS line-clamp). */
  clampLines?: number;
  typographyVariant?: TypographyProps["variant"];
  sx?: TypographyProps["sx"];
  /**
   * Force HTML rendering even if the org flag is off (e.g. admin preview).
   * Default follows noticeBodyFormatting feature flag.
   */
  forceHtml?: boolean;
};

/**
 * Renders notice body: legacy plain with pre-wrap, or sanitized HTML subset
 * when noticeBodyFormatting is enabled.
 */
const NoticeBodyHtml: React.FC<NoticeBodyHtmlProps> = ({
  body,
  clampLines,
  typographyVariant = "body1",
  sx,
  forceHtml = false,
}) => {
  const { isNoticeBodyFormattingEnabled } = useFeatureFlags();
  const allowHtml = forceHtml || isNoticeBodyFormattingEnabled;
  const plain = !allowHtml || isPlainNoticeBody(body || "");
  const safeHtml = useMemo(
    () => (plain ? "" : sanitizeNoticeBodyHtml(body || "")),
    [body, plain],
  );
  const plainDisplay = useMemo(
    () => (plain ? htmlToPlainText(body || "") || body : ""),
    [body, plain],
  );

  if (!body?.trim()) return null;

  const clampSx =
    clampLines && clampLines > 0
      ? {
          display: "-webkit-box",
          WebkitLineClamp: clampLines,
          WebkitBoxOrient: "vertical" as const,
          overflow: "hidden",
        }
      : {};

  if (plain) {
    return (
      <Typography
        variant={typographyVariant}
        sx={{
          whiteSpace: "pre-wrap",
          lineHeight: 1.8,
          color: "text.secondary",
          fontWeight: 400,
          ...clampSx,
          ...(sx as object),
        }}
      >
        {plainDisplay}
      </Typography>
    );
  }

  return (
    <Box
      component="div"
      className="notice-body-html"
      sx={{
        lineHeight: 1.8,
        color: "text.secondary",
        fontSize: (theme) => theme.typography.body2.fontSize,
        fontWeight: 400,
        ...clampSx,
        "& p": { margin: "0 0 0.65em" },
        "& p:last-child": { marginBottom: 0 },
        "& ul, & ol": {
          margin: "0.35em 0 0.65em",
          paddingLeft: "1.35em",
        },
        "& li": { margin: "0.2em 0" },
        "& strong, & b": { fontWeight: 700, color: "text.primary" },
        "& em, & i": { fontStyle: "italic" },
        "& u": { textDecoration: "underline" },
        ...(sx as object),
      }}
      dangerouslySetInnerHTML={{ __html: safeHtml }}
    />
  );
};

export default NoticeBodyHtml;
